import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import IamBreakGlassConsole from '@devnw/cyber-iam/iam/BreakGlassConsole/BreakGlassConsole'
import type {
  BreakGlassActivation,
  BreakGlassEvent,
  EmergencyRole,
} from '@devnw/cyber-iam/iam/types'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'
import { PamLoading, PamError } from '../components/PamStateComponents'
import {
  toIamEmergencyRole,
  toIamBreakGlassEvent,
  type WireEmergencyRole,
  type WireBreakGlassEvent,
} from '../api/breakGlassMapping'

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Action failed'
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

const MutedText = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

/**
 * OIDCPAM Gate-6 Phase 5b: migrated off MUI onto
 * @devnw/cyber-iam/iam/BreakGlassConsole — the exact parity component built
 * from this page. The component now owns the two-phase activation confirm,
 * per-action in-flight guards, and the after-action review form; this page
 * still owns the API calls, the wire-mapping (../api/breakGlassMapping —
 * fixing a latent camelCase/snake_case bug, see that file's header), and the
 * `overdue` computation (host-computed; the library never parses display
 * strings).
 *
 * `refresh({ silent: true })` is used for reloads triggered by
 * `onActionComplete` so the console (and the success note / confirm state it
 * is showing) is never unmounted — only the very first load (or a tenant
 * switch) shows the full-page spinner.
 */
export const BreakGlassConsole = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()
  const [roles, setRoles] = useState<EmergencyRole[]>([])
  const [unreviewed, setUnreviewed] = useState<BreakGlassEvent[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(
    (opts?: { silent?: boolean }): Promise<void> => {
      if (!tenant || tenant === 'system') {
        setLoading(false)
        return Promise.resolve()
      }
      if (!opts?.silent) setLoading(true)
      setError(null)
      return Promise.all([
        client.adminListEmergencyRoles({ tenantId: tenant }),
        client.adminListUnreviewedBreakGlassEvents({ tenantId: tenant }),
      ])
        .then(([r, u]) => {
          setRoles(
            ((r.items ?? []) as unknown as WireEmergencyRole[]).map(
              toIamEmergencyRole
            )
          )
          setUnreviewed(
            ((u.items ?? []) as unknown as WireBreakGlassEvent[]).map(
              toIamBreakGlassEvent
            )
          )
        })
        .catch((e: unknown) => setError(errMessage(e)))
        .finally(() => {
          if (!opts?.silent) setLoading(false)
        })
    },
    [client, tenant]
  )

  useEffect(() => {
    refresh()
      .then(() => {})
      .catch(() => {})
  }, [refresh])

  const handleActivate = async (
    activation: BreakGlassActivation
  ): Promise<void> => {
    await client.adminActivateBreakGlass({
      tenantId: tenant,
      emergencyRoleId: activation.emergencyRoleId,
      justification: activation.justification,
    })
  }

  const handleCompleteReview = async (
    eventId: string,
    notes: string
  ): Promise<void> => {
    await client.adminCompleteBreakGlassReview({
      tenantId: tenant,
      eventId,
      notes,
    })
  }

  const handleActionComplete = (): void => {
    refresh({ silent: true })
      .then(() => {})
      .catch(() => {})
  }

  if (tenant === 'system') {
    return (
      <PageContainer>
        <MutedText>
          Please select a tenant to use the break-glass console.
        </MutedText>
      </PageContainer>
    )
  }

  if (loading) return <PamLoading />

  return (
    <PageContainer>
      {error != null && error !== '' && <PamError message={error} />}
      <IamBreakGlassConsole
        testID="break-glass-console"
        title="Break-Glass Console"
        roles={roles}
        unreviewedEvents={unreviewed}
        onActivate={handleActivate}
        onCompleteReview={handleCompleteReview}
        onActionComplete={handleActionComplete}
      />
    </PageContainer>
  )
}
